import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, attachmentUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";

/**
 * AttachReceiptDialog — attach, replace, or remove a receipt on an existing
 * payment OR expense without re-editing the rest of the row. Backed by
 * `PATCH /api/payments/{pid}/attachment`, which auto-detects whether the id
 * belongs to an invoice payment, bill payment, or expense.
 *
 * Props:
 *   payment   — { id, amount, currency, paid_on, attachment_id, party_name?, invoice_number?, source? }
 *               When null, dialog is closed.
 *   onClose() — close handler
 *   onSaved() — fired after a successful PATCH so the parent can reload
 */
export default function AttachReceiptDialog({ payment, onClose, onSaved }) {
    const [attachment, setAttachment] = useState(null);
    const [saving, setSaving] = useState(false);

    // Hydrate the dialog with whatever the payment row already has so the user
    // can see (and "Remove") an existing receipt without having to re-upload.
    useEffect(() => {
        if (payment?.attachment_id) {
            setAttachment({ id: payment.attachment_id, filename: "Current receipt", size: 0 });
        } else {
            setAttachment(null);
        }
    }, [payment]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/payments/${payment.id}/attachment`, {
                attachment_id: attachment?.id || null,
            });
            toast.success(attachment?.id ? "Receipt updated" : "Receipt removed");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const isExpense = payment?.source === "expense";
    const headerLabel = isExpense
        ? "Expense"
        : payment?.invoice_number || payment?.number || "Payment";

    return (
        <Dialog open={!!payment} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {payment?.attachment_id ? "Replace receipt" : "Attach receipt"}
                    </DialogTitle>
                </DialogHeader>
                {payment && (
                    <div className="space-y-3">
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs">
                            <div className="text-muted-foreground uppercase tracking-wider">{headerLabel}</div>
                            <div className="font-medium text-ocean-700 mt-0.5">{payment.party_name || "—"}</div>
                            <div className="text-muted-foreground mt-0.5">
                                {fmtMoney(payment.amount, payment.currency)} · {fmtDate(payment.paid_on)}
                            </div>
                        </div>
                        <FileUpload
                            value={attachment}
                            onChange={setAttachment}
                            testId="attach-receipt-upload"
                            label="Receipt / supporting doc"
                        />
                        {payment.attachment_id && (
                            <a
                                href={attachmentUrl(payment.attachment_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-secondary-dark hover:text-secondary hover:underline"
                                data-testid={`attach-receipt-view-${payment.id}`}
                            >
                                <ExternalLink className="h-3.5 w-3.5" /> View current receipt
                            </a>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-ocean-700 text-white"
                        data-testid="attach-receipt-save"
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
